
import { errorHeadMeta, errorTitleKey } from '@/utils/errorHead'

let algoliasearchModule = null

const RANGE_MAP = {
  auto: {
    slugPattern: /^(auto|voiture|car)/,
    icon: ['fas', 'car'],
  },
  moto: {
    slugPattern: /^(moto|motorcycle)/,
    icon: ['fas', 'motorcycle'],
  },
  camion: {
    slugPattern: /^(camion|truck|poids-lourd)/,
    icon: ['fas', 'truck'],
  },
  tracteur: {
    slugPattern: /^(tracteur|tractor)/,
    icon: ['fas', 'tractor'],
  },
}

const STOP_WORDS = new Set([
  'les',
  'des',
  'pour',
  'par',
  'une',
  'the',
  'and',
  'for',
  'with',
  'agm',
  'vision',
  'www',
  'http',
  'https',
  'html',
  'php',
  'index',
  'page',
  'com',
  'fr',
])

export default {
  name: 'ErrorPage',
  props: {
    error: { type: Object, required: true },
  },
  data() {
    return {
      suggestedProducts: [],
      suggestedCategories: [],
      suggestedVehicles: [],
      searchSuggestions: [],
      loadingProducts: false,
      detectedRange: null,
    }
  },
  head() {
    return {
      title: this.$t(errorTitleKey(this.error.statusCode)),
      meta: errorHeadMeta(key => this.$t(key)),
    }
  },
  computed: {
    is404() {
      return this.error.statusCode === 404 || this.error.statusCode === 410
    },
    is451() {
      return this.error.statusCode === 451
    },
    isCategoryRestricted() {
      return this.error.entity === 'category'
    },
    displayedStatusCode() {
      return this.is404 ? 404 : this.error.statusCode
    },
    urlSegments() {
      const path = this.$route?.fullPath || ''
      const words = []
      const seen = new Set()

      path
        .split(/[/?&#=]/)
        .filter(Boolean)
        .forEach(segment => {
          segment.split('-').forEach(part => {
            const p = part.toLowerCase().trim()
            if (p && !STOP_WORDS.has(p) && !seen.has(p) && !/^\d+$/.test(p)) {
              seen.add(p)
              words.push(p)
            }
          })
        })

      return words
    },
  },
  mounted() {
    if (this.is404) {
      this.detectRange()
      this.fetchAllSuggestions()
    }
  },
  methods: {
    detectRange() {
      const path = (this.$route?.fullPath || '').toLowerCase()
      for (const [key, config] of Object.entries(RANGE_MAP)) {
        const segments = path.split('/').filter(Boolean)
        if (segments.some(s => config.slugPattern.test(s))) {
          const domains = this.$store.state.domains || []
          const domain = domains.find(
            d => d.slug && d.slug.toLowerCase().includes(key)
          )
          if (domain) {
            this.detectedRange = {
              name: domain.name,
              slug: domain.slug,
              icon: config.icon,
            }
            return
          }
        }
      }
    },

    buildSearchQueries() {
      const segments = [...this.urlSegments]
      if (segments.length === 0) return []

      const queries = []
      const tried = new Set()
      const maxQueries = 5

      const full = segments.join(' ')
      queries.push(full)
      tried.add(full)

      for (
        let len = segments.length - 1;
        len >= 1 && queries.length < maxQueries;
        len--
      ) {
        for (
          let start = 0;
          start <= segments.length - len && queries.length < maxQueries;
          start++
        ) {
          const combo = segments.slice(start, start + len).join(' ')
          if (!tried.has(combo)) {
            queries.push(combo)
            tried.add(combo)
          }
        }
      }

      return queries
    },

    async fetchAllSuggestions() {
      this.loadingProducts = true
      const queries = this.buildSearchQueries()

      try {
        await Promise.all([
          this.fetchProducts(queries),
          this.fetchVehicles(queries),
          this.fetchCategories(queries),
          this.fetchAlgoliaSuggestions(queries),
        ])
      } catch {
        // silent fail
      } finally {
        this.loadingProducts = false
      }
    },

    async fetchProducts(queries) {
      for (const query of queries) {
        if (this.suggestedProducts.length >= 4) return
        try {
          const { data } = await this.$productService.fetchErrorPageRecommendedProducts({
            q: query,
            limit: 12,
          })
          if (data.length >= 4) {
            this.suggestedProducts = data
            return
          }
          if (data.length > this.suggestedProducts.length) {
            this.suggestedProducts = data
          }
        } catch {
          // continue
        }
      }

      if (this.suggestedProducts.length < 4) {
        try {
          const { data } = await this.$productService.fetchErrorPageRecommendedProducts({
            limit: 12,
          })
          if (data.length > 0) this.suggestedProducts = data
        } catch {
          // fallback
        }
      }
    },

    async fetchVehicles(queries) {
      for (const query of queries) {
        if (this.suggestedVehicles.length >= 5) return
        try {
          const { data } = await this.$vehicleService.fetchErrorPageRecommendedVehicles({
            q: query,
            limit: 10,
          })
          if (data.length > this.suggestedVehicles.length) {
            this.suggestedVehicles = data.slice(0, 10)
          }
        } catch {
          // continue
        }
      }
    },

    async fetchCategories(queries) {
      for (const query of queries) {
        if (this.suggestedCategories.length >= 4) return
        try {
          const { data } = await this.$categoryService.fetch({
            'filter[search]': `name\\l=${query}`,
            'fields[categories]': 'id,name,slug,is_active',
            include: 'images,icon,nested_parents_light',
            limit: 8,
          })
          if (data.length > this.suggestedCategories.length) {
            this.suggestedCategories = data
          }
        } catch {
          // continue
        }
      }
    },

    async fetchAlgoliaSuggestions(queries) {
      try {
        if (!algoliasearchModule) {
          algoliasearchModule = (await import('algoliasearch/lite')).default
        }
        const client = algoliasearchModule(
          this.$config.algoliaId,
          this.$config.algoliaApiKey
        )
        const lang = this.$i18n?.locale || 'fr'
        const index = client.initIndex(`products_query_suggestions_${lang}`)

        const suggestions = []
        const tried = new Set()

        for (const query of queries.slice(0, 3)) {
          if (suggestions.length >= 6) break
          const { hits } = await index.search(query, {
            hitsPerPage: 3,
            analytics: false,
          })
          hits.forEach(hit => {
            const q = hit.query || hit.name || ''
            if (q && !tried.has(q.toLowerCase())) {
              tried.add(q.toLowerCase())
              suggestions.push({ query: q })
            }
          })
        }

        if (suggestions.length < 3) {
          const { hits } = await index.search('', {
            hitsPerPage: 6,
            analytics: false,
          })
          hits.forEach(hit => {
            const q = hit.query || hit.name || ''
            if (q && !tried.has(q.toLowerCase())) {
              tried.add(q.toLowerCase())
              suggestions.push({ query: q })
            }
          })
        }

        this.searchSuggestions = suggestions.slice(0, 6)
      } catch {
        // silent fail
      }
    },

    getCategoryPath(cat) {
      const parts = ['/categories']
      let parent = cat.nested_parents_light
      const slugs = []
      while (parent) {
        slugs.unshift(parent.slug)
        parent = parent.nested_parents_light
      }
      parts.push(...slugs, cat.slug)
      return parts.join('/')
    },

    getCategoryImage(cat) {
      if (cat.images && cat.images.length > 0) {
        return (
          cat.images[0].conversions?.thumb ||
          cat.images[0].conversions?.small ||
          cat.images[0].url
        )
      }
      if (cat.icon?.url) return cat.icon.url
      return null
    },
  },
}
